import {useEffect, useState} from 'react';
import {
  requestAnimationFrame,
  cancelAnimationFrame,
} from 'react-viewport-utils';
import {useServerDateNow} from './I18nContext';

/**
 * Defines the refresh rate/ how often the component should update. If `false` it is disabled
 */
type TickInterval = number | false;

/**
 * Triggers a callback with the current Date.now() value once in a tickInterval.
 */
export const useDateNowEffect = (
  cb: (now: number) => void,
  tickInterval: TickInterval = 1000,
) => {
  useEffect(() => {
    let rafTickId = NaN;
    let timeoutTickId = NaN;
    let lastNow = NaN;
    if (tickInterval === false) {
      return;
    }
    const update = () => {
      const now = Date.now();
      const firstTick = isNaN(lastNow);
      const diff = now - (lastNow + tickInterval);
      const exceededTickInterval = firstTick || diff >= 0;
      if (exceededTickInterval) {
        lastNow = now;
        cb(now);
      }
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      updateTick(exceededTickInterval ? tickInterval : tickInterval + diff);
    };

    const updateTick = (delay: number) => {
      timeoutTickId = window.setTimeout(() => {
        // raf avoids ticks to be scheduled if the page is hidden or before a ui update could be triggered
        rafTickId = requestAnimationFrame(update);
      }, delay);
    };

    update();

    return () => {
      clearTimeout(timeoutTickId);
      cancelAnimationFrame(rafTickId);
    };
  }, [cb, tickInterval]);
};

/**
 * Returns the current `Date.now()` value. On hydration, it will use the time on the server when the HTML was generated.
 */
export const useInitialDateNow = () => {
  const serverDateNow = useServerDateNow();
  const [now] = useState(Date.now);

  return serverDateNow || now;
};

export const useDateNow = (tickInterval: TickInterval = 1000) => {
  const initialNow = useInitialDateNow();
  const [now, setNow] = useState(initialNow);
  useDateNowEffect(setNow, tickInterval);

  return now;
};
